// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/sop/SOPIntelligencePanel.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";
import "./SOPIntelligencePanel.css";

const SOP_PROMPTS = [
  "What needs attention now?",
  "What is waiting on review?",
  "What is weakening document control?",
  "What should I review first?",
];

function normalizeCard(value) {
  return value && typeof value === "object" ? value : null;
}


function formatConfidence(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Confidence unavailable";
}

function confidenceTone(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sopLabel(item = {}) {
  return item.item_label || item.label || item.id || item.type || "SOP context";
}

function sourceCountLabel(item = {}) {
  const itemCount = Number(item.item_count);
  if (Number.isFinite(itemCount) && itemCount >= 0) {
    return `${itemCount} represented`;
  }

  const records = Number(item.records);
  if (Number.isFinite(records) && records >= 0) {
    return `${records} loaded`;
  }

  return "";
}

function SOPSkeleton() {
  return (
    <div
      className="sop-ai-panel__skeleton"
      aria-label="Loading SOP Intelligence"
      role="status"
    >
      <span />
      <span />
      <span />
    </div>
  );
}

export default function SOPIntelligencePanel({
  requestJson,
}) {
  const [card, setCard] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(SOP_PROMPTS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

    const [detailsOpen, setDetailsOpen] = useState(false);
  const requestSequenceRef = useRef(0);
  const mountedRef = useRef(true);

  const ask = useCallback(
    async (question, { silent = false } = {}) => {
      if (typeof requestJson !== "function") {
        setError("SOP Intelligence is not available because the SOP API client is not configured.");
        return;
      }

      const requestSequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestSequence;

      setLoading(true);
      setError("");
      setActiveQuestion(question);

      try {
        const payload = await requestJson("/ai/copilot/ask", {
          method: "POST",
          body: {
            scope: "sop",
            question,
            copilot_context: {
              page: "sop_center",
              surface: "sop_page",
              intelligence_mode: "page",
            },
          },
        });

        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) {
          return;
        }

        const nextCard = normalizeCard(payload?.card);
        if (!nextCard) {
          throw new Error("SOP Intelligence returned no grounded response card.");
        }

        setCard(nextCard);
      } catch (err) {
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) {
          return;
        }

        console.error("SOP ambient intelligence error:", err);
        setError(
          String(
            err?.message ||
              err ||
              "Unable to load SOP Intelligence."
          )
        );

      } finally {
        if (
          mountedRef.current &&
          requestSequence === requestSequenceRef.current
        ) {
          setLoading(false);
        }
      }
    },
    [requestJson]
  );

  useEffect(() => {
    mountedRef.current = true;
    ask(SOP_PROMPTS[0], { silent: true });

    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
    // requestJson is the page-owned authenticated API client. The panel refreshes
    // only when that client identity changes. Ordinary SOP row selection does not become ambient intelligence authority.
  }, [ask]);

  const recommendations = Array.isArray(card?.recommended_actions)
    ? card.recommended_actions
    : [];
  const evidence = Array.isArray(card?.evidence_used)
    ? card.evidence_used
    : [];
  const limitations = Array.isArray(card?.known_limitations)
    ? card.known_limitations
    : [];

  const metadata =
    card?.metadata && typeof card.metadata === "object"
      ? card.metadata
      : {};
  const interactionId = String(metadata?.interaction_id || "").trim();
  const auditPersisted = metadata?.audit_persisted === true;
  const responseMode = String(metadata?.ai_response_mode || "").trim();

  return (
    <section
      className="sop-ai-panel"
      aria-labelledby="sop-ai-panel-title"
    >
      <div className="sop-ai-panel__header">
        <div className="sop-ai-panel__heading">
          <div className="sop-ai-panel__eyebrow">OASYS INTELLIGENCE</div>
          <h2 id="sop-ai-panel-title">SOP Intelligence</h2>
          <p>
            Grounded reasoning across SOP document control. Governed lifecycle state, approval queues, overdue detection, acknowledgments, versions, and controlled SOP records remain authoritative.
          </p>
        </div>

        <div className="sop-ai-panel__header-meta">
          <div className="sop-ai-panel__status">
            <StatusChip
              tone={confidenceTone(card?.confidence)}
              dot={Boolean(card)}
            >
              {loading
                ? "Reasoning…"
                : card
                ? formatConfidence(card.confidence)
                : "Awaiting context"}
            </StatusChip>

            {auditPersisted === true ? (
              <StatusChip tone="success">Audited</StatusChip>
            ) : null}

            <Button
              variant="ghost"
              size="small"
              onClick={() => setDetailsOpen((current) => !current)}
              aria-expanded={detailsOpen}
              aria-label={
                detailsOpen
                  ? "Hide intelligence grounding details"
                  : "Show intelligence grounding details"
              }
              className="sop-ai-panel__trust-details"
            >
              {detailsOpen ? "Hide details" : "Details"}
            </Button>
          </div>

        <div className="sop-ai-panel__scope">
          <span>Scope</span>
          <strong>SOP Center</strong>
        </div>
        </div>
      </div>

      <div
        className="sop-ai-panel__prompts"
        role="group"
        aria-label="SOP Intelligence questions"
      >
        {SOP_PROMPTS.map((question) => (
          <button
            key={question}
            type="button"
            className={`sop-ai-panel__prompt ${
              activeQuestion === question ? "is-active" : ""
            }`}
            onClick={() => ask(question)}
            disabled={loading}
            aria-pressed={activeQuestion === question}
          >
            {question}
          </button>
        ))}
      </div>

      <div className="sop-ai-panel__body" aria-live="polite">
        {!card && loading ? (
          <SOPSkeleton />
        ) : (
          <>
            {card ? (
              <div className="sop-ai-panel__answer">
                <div className="sop-ai-panel__answer-topline">
                  <span>SOP brief</span>
                  {loading ? (
                    <span className="sop-ai-panel__refreshing">
                      Refreshing…
                    </span>
                  ) : null}
                </div>

                <div className="sop-ai-panel__summary">
                  {card.summary}
                </div>

                {card.reasoning ? (
                  <p className="sop-ai-panel__reasoning">
                    {card.reasoning}
                  </p>
                ) : null}

                {recommendations.length ? (
                  <div className="sop-ai-panel__actions">
                    <div className="sop-ai-panel__section-label">
                      Operator focus
                    </div>
                    <div className="sop-ai-panel__action-grid">
                      {recommendations.slice(0, 4).map((item, index) => (
                        <div
                          className="sop-ai-panel__action"
                          key={`${String(item)}-${index}`}
                        >
                          <span>{index + 1}</span>
                          <p>{String(item)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {error ? (
              <div className="sop-ai-panel__error" role="alert">
                {error}
              </div>
            ) : null}
          </>
        )}
      </div>

      {detailsOpen ? (
        <div className="sop-ai-panel__details">
          <div className="sop-ai-panel__details-body">
              <dl className="sop-ai-panel__audit-grid">
                <div>
                  <dt>Audit</dt>
                  <dd>{auditPersisted ? "Persisted" : "Pending"}</dd>
                </div>
                <div>
                  <dt>Response mode</dt>
                  <dd>{responseMode || "—"}</dd>
                </div>
                <div>
                  <dt>Interaction</dt>
                  <dd title={interactionId}>{interactionId || "—"}</dd>
                </div>
              </dl>

              {evidence.length ? (
                <div className="sop-ai-panel__detail-section">
                  <div className="sop-ai-panel__section-label">
                    Evidence used
                  </div>
                  <ul>
                    {evidence.map((item, index) => {
                      const count = sourceCountLabel(item);
                      return (
                        <li key={`${item?.id || item?.type || "evidence"}-${index}`}>
                          <span>{sopLabel(item)}</span>
                          {count ? <small>{count}</small> : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {limitations.length ? (
                <div className="sop-ai-panel__detail-section">
                  <div className="sop-ai-panel__section-label">
                    Known limitations
                  </div>
                  <ul>
                    {limitations.map((item, index) => (
                      <li key={`${String(item)}-${index}`}>
                        <span>{String(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
        </div>
      ) : null}
    </section>
  );
}
